import type { BetStatus } from 'src/types/domain/BetStatus'
import type { Marble } from 'src/types/domain/Marble'
import type { RevealedRoundType } from './livePutts'
import { optionAlive, roundTypeAlive } from './liveStatus'
import { PUTTS_PER_ROUND, familyForKey, type PuttMark } from './tileViewModel'

/**
 * B41 — the social feed settles ball by ball.
 *
 * Birdie is the only game in the estate whose outcome resolves AS IT RUNS: an
 * exact pattern is dead the moment a putt contradicts it, a count is dead once
 * the remaining balls cannot reach it. Every other game's feed waits for
 * settlement because every other game's outcome arrives all at once.
 *
 * So LOSSES stream through the round and WINS land on the last ball. That is
 * the same shape as crash, and it is the honest one: nothing is promised early.
 *
 * Two rules hold this module to presentation, and both are load-bearing.
 *
 * 1. IT MUST NOT RUN AHEAD OF THE PICTURE. The feed shows OTHER players' bets.
 *    Marking one lost before the viewer's own screen has shown the putt that
 *    killed it leaks the round — on a surface the player is not even looking
 *    at. Every verdict here is read from what the BOARD IS DRAWING
 *    (`store/birdieLiveBoard`), never from the raw putt rows, so the feed
 *    cannot lead the video by construction rather than by a second copy of the
 *    hold that could drift.
 *
 * 2. THE SERVER REMAINS AUTHORITATIVE ON MONEY. A live verdict is dressing, not
 *    settlement: `effectiveFeedStatus` consults it ONLY while the row is still
 *    pending, so the moment the server answers, its answer stands — silently,
 *    even where the two disagree. A row never flips twice.
 */

/** What the board is drawing right now. Empty marks mean nothing is on screen. */
export interface BirdieLiveBoardView {
  /** The round these marks belong to. A row for any other round is undecided. */
  raceId?: string
  /** The putts the board has ALREADY DRESSED — never the raw rows. */
  marks: readonly PuttMark[]
  /**
   * The drawn type, and only once the board itself is showing it. The publisher
   * clears this while no putt is on screen (`store/birdieLiveBoard`), because
   * the reveal rides the putt rows and must stay behind the same hold.
   */
  revealedType?: RevealedRoundType
}

export const IDLE_LIVE_BOARD: BirdieLiveBoardView = { marks: [] }

/** Decided by the picture so far. `undefined` means the round is still running. */
export type FeedVerdict = 'won' | 'lost'

/**
 * The live verdict for one canonical option key (`IN2`, `HMH`, `FROST`).
 *
 * `undefined` for anything not yet decided, which includes every key before the
 * first putt is on screen and every key this board cannot read — an option we
 * cannot price is never dressed, exactly as the tiles never dim one.
 */
export function liveVerdict(key: string, board: BirdieLiveBoardView): FeedVerdict | undefined {
  // Nothing on screen: nothing is decided, and the round type stays sealed.
  if (board.marks.length === 0) return undefined

  const family = familyForKey(key)
  if (family === undefined) return undefined

  if (family === 'round') {
    // The putts say nothing about this market — it came off the seed before a
    // ball was struck — so it resolves on the reveal alone, or not at all.
    if (board.revealedType === undefined) return undefined
    return roundTypeAlive(key, board.revealedType) ? 'won' : 'lost'
  }

  if (!optionAlive(key, board.marks)) return 'lost'
  // Alive, but a win needs the whole round: two in with one to come is not a win.
  return board.marks.length === PUTTS_PER_ROUND ? 'won' : undefined
}

/**
 * The status a Birdie feed row should RENDER: the server's answer wherever it
 * has one, the live verdict only while the row is still pending.
 *
 * This is the whole of rule 2. A settled row is returned untouched — won, lost,
 * cancelled or cashed out — so a live guess that disagreed with the server
 * loses silently instead of flipping the row a second time.
 */
export function effectiveFeedStatus(
  status: BetStatus,
  key: string,
  board: BirdieLiveBoardView
): BetStatus {
  if (status !== 'pending') return status
  return liveVerdict(key, board) ?? 'pending'
}

/**
 * The canonical key a feed row is backing, or `undefined` if the row is not a
 * Birdie shape.
 *
 * Read exactly as `BirdieFeedPick` reads it — the option row's colour, upper
 * cased — so a tile, a feed pick and a feed verdict can never disagree about
 * one option. ONE selection only: a chip is a bet on this game, and a row with
 * two selections is something this module has no opinion about rather than
 * something it guesses at.
 */
export function birdieOptionKey(
  selectedItems: readonly string[],
  marbles: readonly Marble[]
): string | undefined {
  if (selectedItems.length !== 1) return undefined
  const marble = marbles.find((candidate) => candidate.uuid === selectedItems[0])
  if (marble === undefined) return undefined
  return marble.color.toUpperCase()
}

/**
 * The status one feed row should render, given the round the board is drawing.
 *
 * The race check is the third spoiler guard: a row belongs to a round, and a
 * verdict drawn from THIS round's putts says nothing about a row placed in the
 * last one. Without it the previous tab would redress itself as the new round
 * ran.
 */
export function feedStatusForBet(
  status: BetStatus,
  raceId: string,
  key: string | undefined,
  board: BirdieLiveBoardView
): BetStatus {
  if (key === undefined) return status
  if (board.raceId === undefined || board.raceId !== raceId) return status
  return effectiveFeedStatus(status, key, board)
}

/**
 * The options a feed row selected, tolerating the snake_case the wire has been
 * seen to send. Mirrors the reader `BetTableRow` already uses, so the row's
 * verdict and the running total can never be computed from different picks.
 */
export function birdieSelectedItems(betDetails: unknown): string[] {
  if (betDetails === null || typeof betDetails !== 'object') return []
  const camel = (betDetails as { selectedItems?: unknown }).selectedItems
  if (Array.isArray(camel)) return camel as string[]
  const snake = (betDetails as { selected_items?: unknown }).selected_items
  if (Array.isArray(snake)) return snake as string[]
  return []
}
